// ============================================================
// useGenerateReport.ts
// Crée un job de rapport, attend la notif WebSocket (plus de polling)
// et télécharge le fichier dès réception de report:done.
//
// Résilience WS : si le socket se reconnecte pendant qu'un job est en cours,
// on interroge /reportJob/status une seule fois pour rattraper un REPORT_DONE manqué.
// ============================================================

import { useState, useRef, useEffect, useCallback } from 'react';
import api from '../reduxStore/api';
import { FormatRapport } from '../constants/enums';
import { useSnackbar } from '../components/SnackbarProvider';
import { getSocket } from '../websocket/WebSocketClient';
import { WsEventType, WsPayload, ReportResultPayload } from '../websocket/WsEvents';
import { openReportFile } from '../utils/reportFile';

export interface GenerateReportOptions {
  type_rapport:   string;
  format:         string;
  parametres?:    Record<string, any> | null;
  template_id?:   number | null;
  departement_id?: number | null;
  fileName?:      string;
  /** Clé pour tracker quel item est en cours (ex: numero_doc) */
  key?:           string;
}

interface ActiveJob {
  jobId:    number;
  format:   string;
  fileName: string;
  /** Nettoie le listener WS associé à ce job */
  cleanup:  () => void;
}

/**
 * Récupère le fichier une fois le job DONE.
 * PDF → affichage dans un onglet (inline) ; Excel → téléchargement (attachment).
 * Driver-agnostique : openReportFile gère local (blob) et s3 (presigned URL).
 */
async function downloadFile(jobId: number, format: string, fileName: string): Promise<void> {
  await openReportFile(jobId, {
    mode:     format === FormatRapport.PDF ? 'inline' : 'attachment',
    fileName,
    format,
  });
}

export function useGenerateReport() {
  const [generatingKey, setGeneratingKey] = useState<string | null>(null);
  const { showSnackbar } = useSnackbar();
  const activeJobRef = useRef<ActiveJob | null>(null);

  const generating = generatingKey !== null;

  /** Finalise un job (WS ou fallback reconnexion) — idempotent via activeJobRef */
  const finalizeJob = useCallback(async (
    jobId:         number,
    statut:        'DONE' | 'ERROR',
    error_message: string | null,
  ) => {
    const active = activeJobRef.current;
    if (!active || active.jobId !== jobId) return;

    // Nettoyer immédiatement pour éviter un double traitement
    activeJobRef.current = null;
    active.cleanup();
    setGeneratingKey(null);

    if (statut === 'DONE') {
      try {
        await downloadFile(jobId, active.format, active.fileName);
        showSnackbar('Rapport téléchargé avec succès', 'success');
      } catch {
        showSnackbar('Erreur lors du téléchargement du rapport', 'error');
      }
    } else {
      showSnackbar(error_message || 'Erreur lors de la génération du rapport', 'error');
    }
  }, [showSnackbar]);

  /**
   * Fallback reconnexion WS : si le socket se reconnecte pendant qu'un job
   * est en cours, on interroge /reportJob/status une seule fois pour rattraper
   * un REPORT_DONE qui aurait été émis pendant la fenêtre de déconnexion.
   */
  useEffect(() => {
    let socket: ReturnType<typeof getSocket> | null = null;
    try { socket = getSocket(); } catch { return; }

    const handleReconnect = async () => {
      const active = activeJobRef.current;
      if (!active) return;
      try {
        const res = await api.get('/reportJob/status', { params: { id: active.jobId } });
        const { statut, error_message } = res.data;
        if (statut === 'DONE' || statut === 'ERROR') {
          await finalizeJob(active.jobId, statut, error_message ?? null);
        }
        // PENDING / PROCESSING → le WS notifiera quand ce sera prêt
      } catch {
        // Ignore — le WS notifiera quand le job sera terminé
      }
    };

    socket.on('connect', handleReconnect);
    return () => { socket!.off('connect', handleReconnect); };
  }, [finalizeJob]);

  const generateReport = async (options: GenerateReportOptions): Promise<void> => {
    if (generating) return;

    const { type_rapport, format, parametres, template_id, departement_id, fileName = 'rapport', key } = options;
    const trackKey = key ?? '__default__';

    let socket: ReturnType<typeof getSocket> | null = null;
    try { socket = getSocket(); } catch {
      showSnackbar('WebSocket non disponible — réessayez', 'error');
      return;
    }

    setGeneratingKey(trackKey);

    let jobId = 0; // 0 = pas encore connu (les IDs DB commencent à 1)
    // Stocke un REPORT_DONE arrivé avant que jobId soit connu (worker très rapide)
    let earlyResult: WsPayload<ReportResultPayload> | null = null;

    const handleDone = async (event: WsPayload<ReportResultPayload>) => {
      if (jobId === 0) {
        earlyResult = event;
        return;
      }
      if (event.payload.job_id !== jobId) return;
      await finalizeJob(event.payload.job_id, event.payload.statut, event.payload.error_message);
    };

    socket.on(WsEventType.REPORT_DONE, handleDone);
    const cleanup = () => { socket!.off(WsEventType.REPORT_DONE, handleDone); };

    try {
      const res = await api.post('/reportJob', {
        type_rapport,
        format,
        parametres:     parametres ?? null,
        template_id:    template_id ?? null,
        departement_id: departement_id ?? null,
      });

      jobId = res.data.job_id;

      // Enregistrer le job actif AVANT de vérifier earlyResult (ordre important)
      activeJobRef.current = { jobId, format, fileName, cleanup };

      showSnackbar('Génération du rapport en cours…', 'info');

      // Race condition : REPORT_DONE arrivé pendant le POST → traiter maintenant
      // Cast nécessaire : TS ne trace pas les affectations dans les callbacks async
      const early = earlyResult as WsPayload<ReportResultPayload> | null;
      if (early !== null && early.payload.job_id === jobId) {
        await finalizeJob(jobId, early.payload.statut, early.payload.error_message);
      }

    } catch {
      cleanup();
      setGeneratingKey(null);
      showSnackbar('Erreur lors de la création du rapport', 'error');
    }
  };

  return { generateReport, generating, generatingKey };
}
